import React, { FunctionComponent, useState } from 'react';
import { Column, Row } from '@/components';
import { Select, buildSelectOptions } from '@/components/Select';
import { LabelValue } from '@/types';
import { QUANTITY_RESERVES_OPTIONS } from './constants';
import { ListPacks } from './ListPacks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import { InputV2 } from '@/components/Input';
import { useWindowResize } from '@/hooks';
import { PackI } from '@/containers/ActionServiceV2/utils';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { useAuth } from '@/contexts';
import { SupportedCountriesE } from '@/types/cyclone/models';
import { getCurrencySymbolFromCountry } from '@/utils/money';

type SetupProps = {
  price: number;
  isForSubscriptionEnabled: boolean;
  packs?: PackI[];
  addPack: (pack: PackI) => void;
  deletePack: (id: number) => void;
  isStripeConnected: boolean;
};

type PackFormProps = {
  price: number;
  isForSubscriptionEnabled: boolean;
  addPack: (pack: PackI) => void;
  isStripeConnected: boolean;
  setCreationMode: (value: boolean) => void;
};

const PackForm: FunctionComponent<PackFormProps> = ({
  price: priceService,
  isForSubscriptionEnabled,
  addPack,
  isStripeConnected,
  setCreationMode
}) => {
  const { session } = useAuth();
  const country = session ? session.vendor!.country : SupportedCountriesE.ARGENTINA;
  const currency = getCurrencySymbolFromCountry(country);

  const [limit, setLimit] = useState<LabelValue | undefined>(undefined);
  const [price, setPrice] = useState<string | undefined>('0');
  const [priceUsd, setPriceUsd] = useState<string | undefined>('0');
  const { isMobileSize } = useWindowResize();
  const selectOptionsModality = buildSelectOptions(QUANTITY_RESERVES_OPTIONS, limit, setLimit);

  const handleErrors = (): string | undefined => {
    if (priceService === 0) {
      return 'El precio del servicio no puede ser 0';
    }
    if (isForSubscriptionEnabled) {
      return 'No se pueden agregar packs a un servicio de plan mensual';
    }
    if (price === '0') {
      return 'El precio del pack no puede ser 0';
    } else {
      return undefined;
    }
  };

  const handlerAddPack = () => {
    if (handleErrors()) {
      toast.error(handleErrors());
    } else {
      if (limit && price) {
        setPrice('');
        setPriceUsd('');
        setCreationMode(false);
        setLimit(undefined);
        addPack({ limit: +limit.value, price: +price, ...(priceUsd && { price_usd: +priceUsd }) });
      }
    }
  };

  return (
    <Row
      align="center"
      gap={8}
      className={classNames({
        'w-full': true,
        'flex-col gap-5': isMobileSize
      })}
    >
      <div className="w-full md:w-[calc(25%-8px)]">
        <Select
          placeholder="Cantidad"
          value={limit?.label}
          options={selectOptionsModality}
          disabled={!!!priceService}
          showBackground
        />
      </div>
      <Row
        align="center"
        justify={isMobileSize ? 'space-between' : 'flex-start'}
        gap={8}
        className="w-full md:w-1/4"
      >
        <div
          className={classNames({
            'w-1/2 md:w-full': !isStripeConnected,
            'w-2/5 md:w-1/2': isStripeConnected
          })}
        >
          <InputV2
            id="pack-price-ars"
            placeholder={country}
            onChange={(e) => setPrice(e.target.value)}
            value={price}
            type="number"
            postfix={currency}
            floatLabel
          />
        </div>
        {isStripeConnected && (
          <div className="w-2/5 md:w-1/2">
            <InputV2
              id="pack-price-usd"
              placeholder="Otro país"
              onChange={(e) => setPriceUsd(e.target.value)}
              value={priceUsd}
              type="number"
              postfix="US$"
              floatLabel
            />
          </div>
        )}
        {isMobileSize && (
          <Row
            justify="center"
            align="center"
            onClick={handlerAddPack}
            className={classNames({
              'w-9 h-9 shadow-[0px_3px_4px_0px_rgba(0,0,0,0.3)] rounded-full transition-colors duration-300':
                true,
              'bg-[#0072FB] hover:bg-[#25d366] cursor-pointer': !(
                !!!priceService ||
                isForSubscriptionEnabled ||
                !!!price
              ),
              'bg-[#CACACA] cursor-not-allowed': !!!priceService || isForSubscriptionEnabled || !!!price
            })}
          >
            <FontAwesomeIcon
              icon={faCheck}
              color="#FFF"
              size="1x"
              fixedWidth
              beatFade={!(!!!priceService || isForSubscriptionEnabled || !!!price)}
            />
          </Row>
        )}
      </Row>
      {!isMobileSize && (
        <Row
          justify="center"
          align="center"
          onClick={handlerAddPack}
          className={classNames({
            'w-9 h-9 shadow-[0px_3px_4px_0px_rgba(0,0,0,0.3)] rounded-full transition-colors duration-300':
              true,
            'bg-[#0072FB] hover:bg-[#25d366] cursor-pointer': !(
              !!!priceService ||
              isForSubscriptionEnabled ||
              !!!price
            ),
            'bg-[#CACACA] cursor-not-allowed': !!!priceService || isForSubscriptionEnabled || !!!price
          })}
        >
          <FontAwesomeIcon
            icon={faCheck}
            color="#FFF"
            size="1x"
            fixedWidth
            beatFade={!(!!!priceService || isForSubscriptionEnabled || !!!price)}
          />
        </Row>
      )}
      <Row
        justify="center"
        align="center"
        onClick={() => setCreationMode(false)}
        className="cursor-pointer w-9 h-9 shadow-[0px_3px_4px_0px_rgba(0,0,0,0.3)] bg-[#d3e3ff] hover:bg-[#0072FB] rounded-full transition-colors duration-300"
      >
        <FontAwesomeIcon icon={faXmark} color="#FFF" size="1x" fixedWidth />
      </Row>
    </Row>
  );
};

export const Setup: FunctionComponent<SetupProps> = ({
  packs,
  deletePack,
  price: priceService,
  isForSubscriptionEnabled,
  addPack,
  isStripeConnected
}) => {
  const [creationMode, setCreationMode] = useState<boolean>(false);

  const handleCreationMode = () => {
    if (isForSubscriptionEnabled) {
      toast.error('No se pueden agregar packs a un servicio de plan mensual');
    } else {
      setCreationMode(true);
    }
  };

  return (
    <Column gap={packs?.length === 0 ? 0 : 20} className="w-full">
      <ListPacks packs={packs} deletePack={deletePack} isStripeConnected={isStripeConnected} />
      {creationMode ? (
        <PackForm
          addPack={addPack}
          isForSubscriptionEnabled={isForSubscriptionEnabled}
          isStripeConnected={isStripeConnected}
          price={priceService}
          setCreationMode={setCreationMode}
        />
      ) : (
        <Row onClick={handleCreationMode} align="center" gap={8} className="w-full cursor-pointer">
          <Row
            align="center"
            justify="center"
            gap={8}
            className="w-fit cursor-pointer py-2 px-3 bg-white border- rounded-md shadow-sm border border-[#E9E9E9] hover:border-[#0072FB] transition-colors duration-300"
          >
            <FontAwesomeIcon icon={faPlus} fontSizeAdjust={16} color="#0072FB" />
            <p className="text-base text-[#0072FB]">Agregar precio por pack</p>
          </Row>
        </Row>
      )}
    </Column>
  );
};
